import { z } from 'zod';
import { calendarDateSchema, instantSchema, uuidSchema } from '../schema/rows.js';
const isNode = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
function unsupported(node) {
    throw new Error(`Condición de vista no soportada todavía: ${JSON.stringify(node)}`);
}
const is = (node, field, op) => node.field === field && node.op === op;
/** Reads the known shape of a flow view's filter (docs/08 §7.4). */
export function readViewFilter(filter) {
    const shape = {
        stageKey: null,
        stageCategory: null,
        open: false,
        completedInPeriod: false,
        visibleFromToday: false,
        mineOrUnassigned: false,
    };
    if (!isNode(filter) || !Array.isArray(filter.all))
        unsupported(filter);
    for (const node of filter.all) {
        if (!isNode(node))
            unsupported(node);
        if (is(node, 'stage.key', 'eq') && typeof node.value === 'string') {
            shape.stageKey = node.value;
        }
        else if (is(node, 'stage.category', 'eq') && typeof node.value === 'string') {
            shape.stageCategory = node.value;
        }
        else if (is(node, 'status', 'eq') && node.value === 'open') {
            shape.open = true;
        }
        else if (Array.isArray(node.any)) {
            const branches = node.any;
            const fields = branches.map((b) => (isNode(b) ? b.field : undefined));
            if (fields.includes('status') || branches.some((b) => isNode(b) && Array.isArray(b.all))) {
                // status = open OR (status = done AND completed_at >= $period.start)
                for (const branch of branches) {
                    if (isNode(branch) && is(branch, 'status', 'eq') && branch.value === 'open') {
                        shape.open = true;
                    }
                    else if (isNode(branch) && Array.isArray(branch.all)) {
                        const inner = branch.all;
                        const done = inner.some((n) => isNode(n) && is(n, 'status', 'eq') && n.value === 'done');
                        const since = inner.some((n) => isNode(n) &&
                            is(n, 'completed_at', 'gte') &&
                            isNode(n.value) &&
                            n.value.$period === 'start');
                        if (!done || !since)
                            unsupported(branch);
                        shape.completedInPeriod = true;
                    }
                    else {
                        unsupported(branch);
                    }
                }
            }
            else if (fields.includes('start_on')) {
                shape.visibleFromToday = true;
            }
            else if (fields.includes('assignee_id')) {
                shape.mineOrUnassigned = true;
            }
            else {
                unsupported(node);
            }
        }
        else {
            unsupported(node);
        }
    }
    if (!shape.stageKey === !shape.stageCategory)
        unsupported(filter);
    return shape;
}
export const completedWindowSchema = z.enum(['period', 'today', 'always']);
const viewQueryInput = z.object({
    filter: z.unknown(),
    /** The stage of the view, or every stage of its category. */
    stage_ids: z.array(uuidSchema).min(1),
    user_id: uuidSchema,
    today: calendarDateSchema,
    /** Start of the user's open period. */
    period_start: instantSchema,
    /** Start of today in the user's time zone, for `window: today`. */
    today_start: instantSchema,
    window: completedWindowSchema.default('period'),
    /** Taxonomy whose tags come with each row (`group_by: tag:<key>`); null for none. */
    group_taxonomy_key: z.string().nullable().default('context'),
    /** Custom fields that come with each row (`field:<key>` in the grouping or the order). */
    fields: z
        .array(z.object({ key: z.string().min(1), id: uuidSchema, type: z.enum(['date', 'ref']) }))
        .default([]),
    open_limit: z.number().int().positive().default(500),
    done_limit: z.number().int().positive().default(200),
});
/** Columns of a row and their parameters: the grouping tags and the custom fields. */
function columns(p) {
    const params = [];
    const tags = p.group_taxonomy_key === null
        ? `'[]'`
        : `(SELECT json_group_array(json_object('id', t.id, 'name', t.name, 'sort_key', t.sort_key))
          FROM block_tags bt
          JOIN tags t ON t.id = bt.tag_id
          JOIN taxonomies x ON x.id = t.taxonomy_id
         WHERE bt.block_id = b.id AND x.key = ?)`;
    if (p.group_taxonomy_key !== null)
        params.push(p.group_taxonomy_key);
    // One lookup per field through `field_values.by_block`; the values are read in JS. The unary
    // `+` keeps SQLite off `by_field`, which would read every value of the field for each row.
    const entries = [];
    for (const field of p.fields) {
        if (field.type === 'date') {
            entries.push(`'${field.key}', (SELECT fv.value_date FROM field_values fv
          WHERE fv.block_id = b.id AND +fv.field_id = ? LIMIT 1)`);
            params.push(field.id);
        }
        else {
            entries.push(`'${field.key}', (SELECT fv.value_ref FROM field_values fv
          WHERE fv.block_id = b.id AND +fv.field_id = ? LIMIT 1)`);
            entries.push(`'${field.key}.name', (SELECT t.name FROM field_values fv
          JOIN tags t ON t.id = fv.value_ref
          WHERE fv.block_id = b.id AND +fv.field_id = ? LIMIT 1)`);
            params.push(field.id, field.id);
        }
    }
    return {
        sql: `b.id, b.workspace_id, b.title, b.kind, b.status, b.stage_id, b.stage_changed_at,
       b.due_on, b.start_on, b.sort_key, b.completed_at, b.parent_id, b.created_at,
       ${tags} AS tags,
       json_object(${entries.join(', ')}) AS fields`,
        params,
    };
}
/** The two index-bounded watched queries of a flow view. */
export function compileViewQuery(input) {
    const p = viewQueryInput.parse(input);
    const shape = readViewFilter(p.filter);
    if (p.fields.some((field) => !/^[a-z][a-z0-9_]*$/.test(field.key))) {
        throw new Error('Clave de campo no válida');
    }
    const cols = columns(p);
    // One stage reads `by_stage` in order; several (a category) are merged and ordered in JS.
    const single = p.stage_ids.length === 1;
    const stageCondition = single
        ? 'b.stage_id = ?'
        : `b.stage_id IN (${p.stage_ids.map(() => '?').join(', ')})`;
    const extra = ['b.deleted_at IS NULL', 'b.archived_at IS NULL'];
    const extraParams = [];
    if (shape.visibleFromToday) {
        extra.push('(b.start_on IS NULL OR b.start_on <= ?)');
        extraParams.push(p.today);
    }
    if (shape.mineOrUnassigned) {
        extra.push('(b.assignee_id IS NULL OR b.assignee_id = ?)');
        extraParams.push(p.user_id);
    }
    const conditions = extra.map((c) => `\n   AND ${c}`).join('');
    const open = shape.open
        ? {
            sql: `SELECT ${cols.sql}
  FROM blocks b
 WHERE ${stageCondition} AND b.status = 'open'${conditions}${single ? '\n ORDER BY b.created_at DESC' : ''}
 LIMIT ?`,
            params: [...cols.params, ...p.stage_ids, ...extraParams, p.open_limit],
        }
        : null;
    let done = null;
    if (shape.completedInPeriod) {
        const since = p.window === 'period' ? p.period_start : p.window === 'today' ? p.today_start : null;
        done = {
            sql: `SELECT ${cols.sql}
  FROM blocks b
 WHERE ${stageCondition}${since === null ? '' : ' AND b.completed_at >= ?'} AND b.status = 'done'${conditions}${single ? '\n ORDER BY b.completed_at DESC' : ''}
 LIMIT ?`,
            params: [
                ...cols.params,
                ...p.stage_ids,
                ...(since === null ? [] : [since]),
                ...extraParams,
                p.done_limit,
            ],
        };
    }
    return { open, done };
}
//# sourceMappingURL=view.js.map